import {FC, ReactNode, useState} from 'react';
import {ReactComponent as TicketIcon} from '@icons/sidebar/ticket.svg';
import {ReactComponent as JudgeIcon} from '@icons/sidebar/judge.svg';
import {ReactComponent as MoneyIcon} from '@icons/sidebar/money.svg';
import {ReactComponent as MedalIcon} from '@icons/medal-star.svg';
import {ReactComponent as ImportIcon} from '@icons/import-file.svg';
import {ReactComponent as ArrowRightIcon} from '@icons/arrow-right.svg';
import {ReactComponent as SettingIcon} from '@icons/sidebar/setting.svg';
import {Box, Typography, useMediaQuery} from '@mui/material';
import {dashboardMobStyle} from './MobDashboard.styles';
import {useNavigate} from 'react-router-dom';
import {ITeamRole, ROUTER_URL, TABLE_STATUS} from '@/utils/enums';
import {ScanPassScreen} from '@/shared/components/ScanScreen';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {getCurrentEventSelector} from '@/store/organization-event';
import {getAttendeesStatistics} from '@/store/organization-attendees';
import {toast} from 'react-toastify';
import {getErrorMessage} from '@/utils/helpers';
import {OrganizationAttendeesService} from '@/utils/services';
import Modal from '@/shared/components/Modal';
import {tablesAndTicketsTableStyle} from '@/components/Organization/TablesAndTickets/TablesAndTicketsTable/TablesAndTicketsTable.styles';
import {getTeamSelector} from '@/store/organization';

interface ActionCard {
  id: string;
  label: string;
  icon: ReactNode;
  navigateTo: string;
}

const MobDashboard: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const currentEvent = useAppSelector(getCurrentEventSelector);
  const team = useAppSelector(getTeamSelector);

  const isStandartMember = !!team && team.role === ITeamRole.STANDART;

  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [isNoifyModalOpen, setIsNoifyModalOpen] = useState(false);
  const [isAccessModalOpen, setIsAccessModalOpen] = useState<boolean>(false);

  const isTablet = useMediaQuery('(min-width: 768px)');

  const handleAccessModalChange = () => setIsAccessModalOpen(!isAccessModalOpen);

  const handleNotifyModalChange = () => {
    setIsNoifyModalOpen(!isNoifyModalOpen);
  };

  const handleCheckInComplete = async (guestId: string) => {
    if (!currentEvent?.id) return;
    try {
      await OrganizationAttendeesService.updateAttendeesStatus(+guestId, TABLE_STATUS.CHECKED_IN);
      await dispatch(getAttendeesStatistics(currentEvent.id)).unwrap();
      toast.success('Guest checked in successfully');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const actionCards: ActionCard[] = (
    [
      isTablet && {
        id: 'add-event',
        label: 'Add Event',
        icon: <JudgeIcon />,
        navigateTo: `${ROUTER_URL.ORGANIZATION_ADD_EVENT}`,
      },
      {id: 'scan', label: 'Scan Pass', icon: <TicketIcon />, navigateTo: ''},
      {
        id: 'tickets',
        label: 'Purchase Tickets',
        icon: <TicketIcon />,
        navigateTo: `${ROUTER_URL.ORGANIZATION_PURCHASE_TICKETS}`,
      },
      isTablet && {
        id: 'tableAndTickets',
        label: 'Table and Tickets',
        icon: <TicketIcon />,
        navigateTo: `${ROUTER_URL.ORGANIZATION_TABLES}`,
      },
      {
        id: 'checkIn',
        label: 'Check In (Attendees)',
        icon: (
          <Box sx={{rotate: '-90deg'}}>
            <ImportIcon />
          </Box>
        ),
        navigateTo: `${ROUTER_URL.ORGANIZATION_ATTENDEES}`,
      },
      {id: 'fund', label: 'Fund the Mission', icon: <MoneyIcon />, navigateTo: `${ROUTER_URL.ORGANIZATION_FUND}`},
      {
        id: 'auction-entry',
        label: 'Auction Entry',
        icon: <MedalIcon />,
        navigateTo: `${ROUTER_URL.ORGANIZATION_AUCTION_ENTRY}`,
      },
      {
        id: 'auction-checkout',
        label: 'Auction Checkout',
        icon: <JudgeIcon />,
        navigateTo: `${ROUTER_URL.ORGANIZATION_CHECK_OUT}`,
      },
      isTablet && {
        id: 'event-settings',
        label: 'Event Settings',
        icon: <SettingIcon />,
        navigateTo: `${ROUTER_URL.ORGANIZATION_EVENT_SETTINGS}`,
      },
    ] as (ActionCard | false)[]
  ).filter(Boolean) as ActionCard[];

  return (
    <Box sx={dashboardMobStyle.wrapper}>
      {actionCards.map((card) => (
        <Box
          key={card.id}
          sx={{
            ...dashboardMobStyle.card,
            ...(card.id === 'event-settings' && !currentEvent ? dashboardMobStyle.cardDisabled : {}),
          }}
          onClick={() => {
            if (card.id === 'event-settings' && !currentEvent) return;
            if ((card.id === 'add-event' || card.id === 'tickets') && isStandartMember) {
              handleAccessModalChange();
              return;
            }

            if (card.id === 'scan') {
              setIsScannerOpen(true);
            } else if (card.id === 'tickets') {
              if (!currentEvent?.tableInfos.length) {
                handleNotifyModalChange();
                return;
              }
              navigate(card.navigateTo);
            } else {
              navigate(card.navigateTo);
            }
          }}
        >
          <Box sx={dashboardMobStyle.textWrapper}>
            <Box sx={dashboardMobStyle.icon}>{card.icon}</Box>
            <Typography sx={dashboardMobStyle.text}>{card.label}</Typography>
          </Box>
          <Box sx={dashboardMobStyle.arrow}>
            <ArrowRightIcon />
          </Box>
        </Box>
      ))}
      {isScannerOpen && (
        <Box sx={{position: 'fixed', inset: 0, zIndex: 1300}}>
          <ScanPassScreen
            open={isScannerOpen}
            onClose={() => setIsScannerOpen(false)}
            onCheckInComplete={handleCheckInComplete}
          />
        </Box>
      )}
      {/* <Box sx={{height: '100vh', width: '100%'}}>
        <ScanPassScreen
          open={isScannerOpen}
          onClose={() => setIsScannerOpen(false)}
          onCheckInComplete={handleCheckInComplete}
        />
      </Box> */}
      <Modal
        onClose={handleNotifyModalChange}
        open={isNoifyModalOpen}
        title="Action needed"
        description="Add a table tier before you can assign guests to a table."
        sx={tablesAndTicketsTableStyle.modalNotify}
      />
      <Modal
        open={isAccessModalOpen}
        onClose={handleAccessModalChange}
        title="Access Denied"
        description="You do not have access to this feature"
        sx={tablesAndTicketsTableStyle.modalNotify}
      />
    </Box>
  );
};

export default MobDashboard;
